import {
  Box,
  Button,
  Paper,
  Typography,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import * as React from "react";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ProductCarousel from "./ProductCarouselComponent";
import MeetingModal from "./MeetingModal";
import MobileFriendlyIcon from "@mui/icons-material/MobileFriendly";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import ShieldIcon from "@mui/icons-material/Shield";
import Player from "./AudioRedesigned";
import useGAEventsTracker from "./GAEventsHook";
import BannerSection from "./BannerSection";
import { CenterBox } from "./commons/BoxAbstraction";

export default function VizAppPage() {
  const GAEventsTracker = useGAEventsTracker("Vizapp Activity");
  const [showMeeting, setShowMeeting] = React.useState(false);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isExtraSmallScreen = useMediaQuery("(max-width:400px)");
  
  // Background SVG path - same as user journey page
  const backgroundSvgPath = "/images/userJourney/assets/job297-ploy-07c-blue-01.svg";
  const slides = [
    {
      title: isSmallScreen ? "Coverage" : "Complete Report Coverage",
      heading: "Complete Report Coverage",
      description:
        "Almost all biomarkers within hematology and biochemistry are covered. We are open to adding more parameters.",
      illustration: "vizapp4.png",
      audio: "",
      sample: "",
    },
    {
      title: isSmallScreen ? "Interactive" : "Interactive & Searchable",
      heading: "Interactive & Searchable",
      description:
        "Users can click on the human body, navigation carousel & search bar to quickly check the profile of their choice. There's also a toggle button to change the view to 'abnormal only' results.",
      illustration: "vizapp3.png",
      audio: "",
      sample: "",
    },
    {
      title: isSmallScreen ? "Media" : "Shop & Media Sections",
      heading: "Shop & Media Sections",
      description:
        "Users can learn more about concerning test profiles through curated articles & videos. Our smart recommender suggests the top products & services that they should avail to keep track of their health.",
      illustration: "vizapp2.png",
      audio: "",
      sample: "",
    },
    {
      title: isSmallScreen ? "Analytics" : "User Analytics",
      heading: "User Analytics",
      description:
        "Detailed user analytics & recommendations around product use as well as risk profiles will be provided to you on a monthly basis, to help you target your interested users better.",
      illustration: "vizapp1.svg",
      audio: "",
      sample: "",
    },
  ];

  const bannerContent = {
    title: "Viz App",
    description:
      "Web-based Smart Report application with world-class UI/UX and features. Instant, secure and mobile-friendly, it’s a product for the 21st century.",
    image: "vizAppImage.png",
    styles: { imageWidth: isSmallScreen ? "150px" : "343px" },
    features: [
      {
        logo: (
          <MobileFriendlyIcon
            sx={{ color: "#3567D6", fontSize: isSmallScreen ? "8px" : "30px" }}
          />
        ),
        text: "Mobile-friendly",
      },
      {
        logo: (
          <ShuffleIcon
            sx={{ color: "#3567D6", fontSize: isSmallScreen ? "8px" : "30px" }}
          />
        ),
        text: "Flexible Design",
      },
      {
        logo: (
          <ShieldIcon
            sx={{ color: "#3567D6", fontSize: isSmallScreen ? "8px" : "30px" }}
          />
        ),
        text: "Fully Secure",
      },
    ],
    buttonText: "Try Now",
    link: "https://www.demo-vizapp.niroggyan.com/overview?id=5a5e63561560607acca2c7eee9d6fb913e48b7be5da3c45feee92b7faf8f77d1",
    gaEvents: {
      category: "Vizapp Activity",
      action: "Vizapp opened",
    },
  };

  const vizAppFeatures = {
    feature1: {
      title1: "Complete Report",
      title2: "Coverage",
      description:
        "Almost all biomarkers within hematology and biochemistry are covered. We are open to adding more parameters.",
      image1: isSmallScreen
        ? "reportCoverage1Mobile.png"
        : "reportCoverage1.png",
      image2: isSmallScreen
        ? "reportCoverage2Mobile.png"
        : "reportCoverage2.png",
    },
    feature2: {
      title1: "Interactive &",
      title2: "Searchable",
      description:
        "Users can click on the human body, navigation carousel & search bar to quickly check the profile of their choice.",
      image1: isSmallScreen ? "interactive1Mobile.png" : "interactive1.png",
      image2: isSmallScreen ? "interactive2Mobile.png" : "interactive2.png",
    },
    feature3: {
      title1: "Shop & Media",
      title2: "Section",
      description:
        " Our smart recommender suggests the top products & services that they should avail to keep track of their health.",
      image1: "shopMedia1.png",
      image2: "shopMedia2.png",
    },
    feature4: {
      title1: "AI Risk",
      title2: "Analysis",
      description:
        "You can scan or click on QR code to view your complete Risk Profile.",
      image1: "aiRiskAnalysis.png",
      image2: "userAnalytics2.png",
    },
  };

  const CardWrapper = ({ children }) => {
    return (
      <Box
        sx={{
          width: isSmallScreen
            ? isExtraSmallScreen
              ? "166px"
              : "185px"
            : "265px",
          height: isSmallScreen ? "347px" : "550px",
          background: "#022674",
          borderRadius: "20px",
          boxShadow: "0px 4px 4px 2px rgba(0,0,0,0.25)",
          position: "relative",
        }}
      >
        {children}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#ffffff",
        overflowX: "hidden",
        // Background SVG - same as user journey page
        backgroundImage: `url(${backgroundSvgPath})`,
        backgroundSize: "cover",
        backgroundPosition: "center top",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "scroll",
      }}
    >
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          padding: { xs: "77px 16px 40px", sm: "96px 24px 40px", md: "56px 48px 40px" },
        }}
      >
        <BannerSection data={bannerContent} />
      <Box
        sx={{
          background: !isSmallScreen ? "rgba(64, 123, 255, 0.23)" : "",
          boxShadow: !isSmallScreen ? "4px 4px 2.1px 0px rgba(0,0,0,0.25)" : "",
          paddingY: !isSmallScreen && "24px",
          borderRadius: "40px",
          marginTop: "38px",
        }}
      >
        <Typography
          sx={{ textAlign: "center" }}
          mb={isSmallScreen ? "16px" : "27px"}
          variant={isSmallScreen ? "body3" : "h3"}
        >
          Viz App Features
        </Typography>
        <CenterBox
          sx={{
            gap: isSmallScreen ? "10px" : "24px",
            flexWrap: isSmallScreen ? "wrap" : "",
          }}
        >
          <CardWrapper sx={{ display: "flex", flexDirection: "column" }}>
            <Box sx={{ padding: "10px 0px 0px 14px" }}>
              <Typography
                style={{ color: "#7BB58C" }}
                variant={isSmallScreen ? "h1Mobile" : "h3"}
              >
                {vizAppFeatures.feature1.title1}
              </Typography>
              <Typography
                style={{ color: "#fff" }}
                variant={isSmallScreen ? "h1Mobile" : "h3"}
              >
                {vizAppFeatures.feature1.title2}
              </Typography>
            </Box>
            <Box
              sx={{
                position: "relative",
                height: isSmallScreen ? "" : "360px",
                order: isSmallScreen ? "2" : "",
              }}
            >
              <Box
                component="img"
                alt="report-coverage"
                src={`/images/${vizAppFeatures.feature1.image1}`}
                sx={{
                  position: "absolute",
                  top: isSmallScreen ? "-18px" : "10px",
                  left: 0,
                }}
              />
              <Box
                component="img"
                alt="report-coverage"
                src={`/images/${vizAppFeatures.feature1.image2}`}
                sx={{
                  position: "absolute",
                  top: isSmallScreen ? "92px" : "-90px",
                  right: 0,
                }}
              />
            </Box>
            <Typography
              variant={isSmallScreen ? "h4Mobile" : "body2"}
              sx={{
                color: "#fff",
                padding: "0px 6px 9px 14px",
                marginTop: isSmallScreen ? "6px" : "",
              }}
            >
              {vizAppFeatures.feature1.description}
            </Typography>
          </CardWrapper>

          <CardWrapper>
            {isSmallScreen && (
              <Box
                sx={{
                  padding: isSmallScreen ? "10px 14px" : "0px 0px 0px 100px",
                  marginTop: isSmallScreen ? "" : "36px",
                }}
              >
                <Typography
                  style={{ color: "#7BB58C" }}
                  variant={isSmallScreen ? "h1Mobile" : "h3"}
                >
                  {vizAppFeatures.feature2.title1}
                </Typography>
                <Typography
                  style={{ color: "#fff" }}
                  variant={isSmallScreen ? "h1Mobile" : "h3"}
                >
                  {vizAppFeatures.feature2.title2}
                </Typography>
              </Box>
            )}
            <Typography
              variant={isSmallScreen ? "h4Mobile" : "body2"}
              sx={{
                color: "#fff",
                padding: isSmallScreen
                  ? "0px 10px 0px 10px"
                  : "16px 12px 0px 75px",
                textAlign: isSmallScreen ? "" : "right",
              }}
            >
              {vizAppFeatures.feature2.description}
            </Typography>
            {!isSmallScreen && (
              <Box
                sx={{
                  padding: "0px 0px 0px 100px",
                  marginTop: "36px",
                }}
              >
                <Typography
                  style={{ color: "#7BB58C" }}
                  variant={isSmallScreen ? "h3Mobile" : "h3"}
                >
                  {vizAppFeatures.feature2.title1}
                </Typography>
                <Typography
                  style={{ color: "#fff" }}
                  variant={isSmallScreen ? "h3Mobile" : "h3"}
                >
                  {vizAppFeatures.feature2.title2}
                </Typography>
              </Box>
            )}
            <Box>
              <Box
                component="img"
                alt="report-coverage"
                src={`/images/${vizAppFeatures.feature2.image1}`}
                sx={{
                  position: "absolute",
                  top: isSmallScreen ? "150px" : "-24px",
                  left: 0,
                }}
              />
              <Box
                component="img"
                alt="report-coverage"
                src={`/images/${vizAppFeatures.feature2.image2}`}
                sx={{
                  position: "absolute",
                  bottom: isSmallScreen ? "25px" : "0px",
                  right: 0,
                  borderRadius: "0px 0px 20px 20px",
                }}
              />
            </Box>
          </CardWrapper>

          <CardWrapper>
            <Box
              sx={{
                padding: isSmallScreen
                  ? "10px 14px 0px 14px"
                  : "32px 14px 0px 14px",
              }}
            >
              <Typography
                style={{ color: "#7BB58C" }}
                variant={isSmallScreen ? "h1Mobile" : "h3"}
              >
                {vizAppFeatures.feature3.title1}
              </Typography>
              <Typography
                style={{ color: "#fff" }}
                variant={isSmallScreen ? "h1Mobile" : "h3"}
              >
                {vizAppFeatures.feature3.title2}
              </Typography>
            </Box>
            <Typography
              variant={isSmallScreen ? "h4Mobile" : "body2"}
              sx={{
                color: "#fff",
                padding: isSmallScreen
                  ? "0px 86px 0px 14px"
                  : "0px 129px 0px 14px",
                marginTop: isSmallScreen ? "6px" : "27px",
              }}
            >
              {vizAppFeatures.feature3.description}
            </Typography>
            <Box>
              <Box
                component="img"
                alt="report-coverage"
                src={`/images/${vizAppFeatures.feature3.image1}`}
                sx={{
                  position: "absolute",
                  top: "52px",
                  right: 0,
                  zIndex: 10,
                  width: isSmallScreen ? "80%" : "90%",
                }}
              />
              <Box
                component="img"
                alt="report-coverage"
                src={`/images/${vizAppFeatures.feature3.image2}`}
                sx={{
                  position: "absolute",
                  bottom: "0px",
                  right: 0,
                  borderRadius: "0px 0px 20px 20px",
                  width: isSmallScreen ? "100%" : "100%",
                }}
              />
            </Box>
          </CardWrapper>

          <CardWrapper>
            <Box
              sx={{
                height: isSmallScreen ? "200px" : "347px",
                padding: "8px 18px 0px 0px",
              }}
            >
              <Box
                component="img"
                alt="report-coverage"
                src={`/images/${vizAppFeatures.feature4.image1}`}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 12,
                  width: isSmallScreen ? "70%" : "90%",
                }}
              />
              <Box
                component="img"
                alt="report-coverage"
                src={`/images/${vizAppFeatures.feature4.image2}`}
                sx={{
                  position: "absolute",
                  top: isSmallScreen
                    ? isExtraSmallScreen
                      ? "114px"
                      : "120px"
                    : "163px",
                  left: 0,
                  height: isSmallScreen
                    ? isExtraSmallScreen
                      ? "36%"
                      : "40%"
                    : "41%",
                }}
              />
            </Box>
            <Box
              sx={{
                position: "absolute",
                right: isSmallScreen ? "8px" : "25px",
                top: isSmallScreen ? "" : "277px",
              }}
            >
              <Typography
                style={{ color: "#7BB58C", textAlign: "right" }}
                variant={isSmallScreen ? "h1Mobile" : "h3"}
              >
                {vizAppFeatures.feature4.title1}
              </Typography>
              <Typography
                style={{ color: "#fff" }}
                variant={isSmallScreen ? "h1Mobile" : "h3"}
              >
                {vizAppFeatures.feature4.title2}
              </Typography>
            </Box>
            <Typography
              variant={isSmallScreen ? "h4Mobile" : "body2 "}
              sx={{
                color: "#fff",
                padding: isSmallScreen
                  ? "0px 0px 0px 32px"
                  : "0px 0px 0px 32px",
                position: "absolute",
                top: isSmallScreen ? "264px" : "410px",
                right: isSmallScreen ? "8px" : "20px",
                textAlign: "right",
              }}
            >
              {vizAppFeatures.feature4.description}
            </Typography>
          </CardWrapper>
        </CenterBox>
      </Box>
      </Box>
    </Box>
  );
}
